import {
  IonButton,
  IonIcon,
  IonPage,
  IonRouterOutlet,
  useIonRouter,
} from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useHistory, useParams, useRouteMatch } from 'react-router';
import { useGetRoleQuery } from '../../api/graphql/generated';
import { RoleFormProvider } from '../../api/hooks/role.hooks';
import { NestedPageHeader } from '../../components/header/page.header';
import { RoleDetailModal } from '../../components/role/role-detail-modal';
import { RoleEntities } from '../../components/role/role-entities';
import { useBeaconRouter } from '../../route/route.hooks';
import { RoleEntityPermissionsPage } from './role-entity-permissions.page';

interface RouteParams {
  id: string;
}

interface RolePageProps {
  defaultBackRoute?: string;
}

const RolePage = (props: RolePageProps): ReactElement => {
  const { defaultBackRoute } = props;

  const { id } = useParams<RouteParams>();
  const { i18n } = useTranslation();

  const { builder } = useBeaconRouter();
  const { push } = useIonRouter();
  const { replace } = useHistory();
  const { path, url } = useRouteMatch();

  const [isOpen, setIsOpen] = useState(false);

  const selectPermission = useCallback(
    (permissionEntity: string) => {
      push(`${url}/${permissionEntity}`);
    },
    [push, url]
  );

  const { data: getRoleData } = useGetRoleQuery({
    variables: {
      input: {
        id,
      },
    },
  });

  const role = getRoleData?.getRole;

  return (
    <IonPage>
      <RoleFormProvider id={id}>
        <IonRouterOutlet id="role-pages">
          <Route path={path} exact>
            <IonPage>
              <NestedPageHeader
                title={role?.name ?? i18n.t('common:customize_role')}
                defaultBackRoute={defaultBackRoute ?? builder.getHomeRoute()}
                rightButtons={
                  <IonButton
                    slot="icon-only"
                    onClick={() => {
                      setIsOpen(true);
                    }}
                  >
                    <IonIcon icon={ellipsisVertical} />
                  </IonButton>
                }
              />
              <RoleEntities onSelectedPermissionEntity={selectPermission} />
              <RoleDetailModal
                isOpen={isOpen}
                role={role}
                onDidDismiss={() => {
                  setIsOpen(false);
                }}
                onDelete={() => {
                  replace(builder.getAdminRolesRoute());
                }}
              />
            </IonPage>
          </Route>
          <Route path={`${path}/:entity`} exact>
            <RoleEntityPermissionsPage />
          </Route>
        </IonRouterOutlet>
      </RoleFormProvider>
    </IonPage>
  );
};

export { RolePage };
